import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

// Configuration - Set your analytics ID here
const ANALYTICS_CONFIG = {
  // Google Analytics 4 Measurement ID (e.g., 'G-XXXXXXXXXX')
  googleAnalyticsId: import.meta.env.VITE_GA_ID || '',

  // Enable/disable analytics (useful for development)
  enabled: import.meta.env.PROD || import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
};

// Declare gtag for TypeScript
declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

// Track if GA has been initialized
let gaInitialized = false;

/**
 * Initialize Google Analytics using Google's standard approach
 * See: https://developers.google.com/analytics/devguides/collection/gtagjs
 */
function initGA(measurementId: string) {
  if (typeof window === 'undefined' || !measurementId || gaInitialized) return;

  // Initialize dataLayer FIRST (Google's standard approach)
  window.dataLayer = window.dataLayer || [];

  // Define gtag function that pushes to dataLayer
  // This must use 'arguments' object, not rest params, per Google's spec

  window.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  // Set consent (must be before config)
  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
  });

  // Initialize gtag with current timestamp
  window.gtag('js', new Date());

  // Configure the measurement ID
  window.gtag('config', measurementId, {
    send_page_view: false, // We'll track manually for SPA
  });

  // Load the gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  gaInitialized = true;
}

/**
 * Track page view
 */
export function trackPageView(path: string, title?: string) {
  if (!ANALYTICS_CONFIG.enabled || !ANALYTICS_CONFIG.googleAnalyticsId) return;

  // Initialize GA if not already done
  if (!gaInitialized) {
    initGA(ANALYTICS_CONFIG.googleAnalyticsId);
  }

  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title || document.title,
    });
  }

  // Console log in development
  if (import.meta.env.DEV) {
    console.log('[Analytics] Page view:', path);
  }
}

/**
 * Track custom event
 */
export function trackEvent(eventName: string, params?: Record<string, unknown>) {
  if (!ANALYTICS_CONFIG.enabled || !ANALYTICS_CONFIG.googleAnalyticsId) return;

  if (window.gtag) {
    window.gtag('event', eventName, params);
  }

  if (import.meta.env.DEV) {
    console.log('[Analytics] Event:', eventName, params);
  }
}

// ========================================
// ADVANCED TRACKING FUNCTIONS
// ========================================

/**
 * Track sutta reading with details
 */
export function trackSuttaRead(suttaId: string, suttaTitle: string, nikaya: string) {
  trackEvent(AnalyticsEvents.SUTTA_READ, {
    sutta_id: suttaId,
    sutta_title: suttaTitle,
    nikaya: nikaya,
  });
}

/**
 * Track reading progress (scroll depth)
 */
export function trackReadingProgress(suttaId: string, progressPercent: number) {
  // Only track at 25%, 50%, 75%, 100% milestones
  const milestone = Math.floor(progressPercent / 25) * 25;
  if (milestone > 0 && milestone <= 100) {
    trackEvent(AnalyticsEvents.READING_PROGRESS, {
      sutta_id: suttaId,
      progress_percent: milestone,
    });
  }
}

/**
 * Track reading time when user leaves a sutta page
 */
export function trackReadingTime(suttaId: string, timeSeconds: number) {
  // Only track if user spent at least 5 seconds
  if (timeSeconds >= 5) {
    trackEvent(AnalyticsEvents.READING_TIME, {
      sutta_id: suttaId,
      time_seconds: Math.round(timeSeconds),
      time_category: getTimeCategory(timeSeconds),
    });
  }
}

function getTimeCategory(seconds: number): string {
  if (seconds < 30) return 'brief';
  if (seconds < 120) return 'short';
  if (seconds < 300) return 'medium';
  if (seconds < 600) return 'long';
  return 'extended';
}

/**
 * Track search queries
 */
export function trackSearch(query: string, resultsCount: number) {
  trackEvent(AnalyticsEvents.SEARCH_PERFORMED, {
    search_term: query,
    results_count: resultsCount,
  });
}

/**
 * Track bookmark actions
 */
export function trackBookmark(suttaId: string, action: 'add' | 'remove') {
  trackEvent(action === 'add' ? AnalyticsEvents.BOOKMARK_ADD : AnalyticsEvents.BOOKMARK_REMOVE, {
    sutta_id: suttaId,
  });
}

/**
 * Track user preference changes
 */
export function trackPreferenceChange(
  preferenceName: string,
  oldValue: string | number | boolean,
  newValue: string | number | boolean
) {
  trackEvent(AnalyticsEvents.SETTINGS_CHANGED, {
    preference_name: preferenceName,
    old_value: String(oldValue),
    new_value: String(newValue),
  });
}

/**
 * Track navigation between suttas
 */
export function trackNavigation(
  fromSutta: string,
  toSutta: string,
  method: 'next' | 'prev' | 'link' | 'search'
) {
  trackEvent(AnalyticsEvents.NAVIGATION, {
    from_sutta: fromSutta,
    to_sutta: toSutta,
    navigation_method: method,
  });
}

/**
 * Track share actions
 */
export function trackShare(
  suttaId: string,
  shareMethod: 'copy' | 'twitter' | 'facebook' | 'whatsapp' | 'other'
) {
  trackEvent(AnalyticsEvents.SHARE_CLICKED, {
    sutta_id: suttaId,
    share_method: shareMethod,
  });
}

/**
 * Track view mode changes (Pali, Kannada, Pali-Kannada, Summary, Notes tabs)
 */
export function trackViewModeChange(
  suttaId: string,
  viewMode: 'pali' | 'kannada' | 'pali-kannada' | 'summary' | 'notes'
) {
  trackEvent(AnalyticsEvents.VIEW_MODE_CHANGED, {
    sutta_id: suttaId,
    view_mode: viewMode,
  });
}

/**
 * Track feature usage (generic function for any feature)
 */
export function trackFeatureUsage(featureName: string, details?: Record<string, unknown>) {
  trackEvent(AnalyticsEvents.FEATURE_USED, {
    feature_name: featureName,
    ...details,
  });
}

/**
 * Track error events
 */
export function trackError(errorType: string, errorMessage: string, errorLocation?: string) {
  trackEvent(AnalyticsEvents.ERROR_OCCURRED, {
    error_type: errorType,
    error_message: errorMessage.slice(0, 100), // Limit message length
    error_location: errorLocation,
  });
}

// ========================================
// HOOKS FOR AUTOMATIC TRACKING
// ========================================

/**
 * Hook to track page views on route change
 */
export function useAnalytics() {
  const location = useLocation();
  const isFirstRender = useRef(true);

  // Initialize GA on first mount
  useEffect(() => {
    if (ANALYTICS_CONFIG.enabled && ANALYTICS_CONFIG.googleAnalyticsId) {
      initGA(ANALYTICS_CONFIG.googleAnalyticsId);
      // Track initial page view
      trackPageView(location.pathname + location.search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track page views on route change (skip first render as it's handled above)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (ANALYTICS_CONFIG.enabled) {
      trackPageView(location.pathname + location.search);
    }
  }, [location]);
}

/**
 * Hook to track reading time on a page
 * Call this in your sutta reader component
 */
export function useReadingTimeTracker(suttaId: string | undefined) {
  const startTimeRef = useRef<number>(0);

  useEffect(() => {
    if (!suttaId) return;

    // Set start time when effect runs (not during render)
    startTimeRef.current = Date.now();

    return () => {
      const timeSpent = (Date.now() - startTimeRef.current) / 1000;
      trackReadingTime(suttaId, timeSpent);
    };
  }, [suttaId]);
}

/**
 * Hook to track scroll progress on a page
 * Call this in your sutta reader component
 */
export function useScrollProgressTracker(suttaId: string | undefined) {
  const trackedMilestones = useRef(new Set<number>());

  const handleScroll = useCallback(() => {
    if (!suttaId) return;

    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;

    // Track at 25, 50, 75, 100% milestones
    [25, 50, 75, 100].forEach(milestone => {
      if (scrollPercent >= milestone && !trackedMilestones.current.has(milestone)) {
        trackedMilestones.current.add(milestone);
        trackReadingProgress(suttaId, milestone);
      }
    });
  }, [suttaId]);

  useEffect(() => {
    if (!suttaId) return;

    // Reset tracked milestones when sutta changes
    trackedMilestones.current.clear();

    // Use passive listener for better scroll performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [suttaId, handleScroll]);
}

// Predefined events for common actions
export const AnalyticsEvents = {
  // Core events
  SUTTA_READ: 'sutta_read',
  READING_PROGRESS: 'reading_progress',
  READING_TIME: 'reading_time',

  // User actions
  BOOKMARK_ADD: 'bookmark_add',
  BOOKMARK_REMOVE: 'bookmark_remove',
  SEARCH_PERFORMED: 'search_performed',
  SETTINGS_CHANGED: 'settings_changed',
  SHARE_CLICKED: 'share_clicked',
  NAVIGATION: 'navigation',
  VIEW_MODE_CHANGED: 'view_mode_changed',
  FEATURE_USED: 'feature_used',

  // System
  ERROR_OCCURRED: 'error_occurred',
};
